import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { UserContext } from '../userContext';
import {
  FaHeart, FaSearch, FaPlay, FaFilePdf, FaImage, FaCalendarAlt,
  FaUser, FaEye, FaUserGraduate, FaArrowRight, FaHome, FaRegHeart,
  FaTrash, FaTimes, FaBookmark, FaCheckCircle
} from 'react-icons/fa';
import './MeusFavoritos.css';
import { normalizeText } from '../utils/text';

function MeusFavoritos() {
  const navigate = useNavigate();
  const { user, userFavorites, toggleFavorite } = useContext(UserContext);

  const [materiaisPorSubcategoria, setMateriaisPorSubcategoria] = useState({});
  const [conteudosFavoritados, setConteudosFavoritados] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalMateriais, setTotalMateriais] = useState(0);
  const [totalConteudosFavoritos, setTotalConteudosFavoritos] = useState(0);

  // Função para verificar se um link é YouTube
  const isYouTubeLink = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Função para verificar se um link é Vimeo
  const isVimeoLink = (url) => {
    if (!url) return false;
    return url.includes('vimeo.com');
  };

  // Função para obter a URL do material considerando linkRelacionado
  const getMaterialUrl = (material) => {
    // Se é material de aluno e tem linkRelacionado com YouTube/Vimeo, usar ele
    if (material.source === 'student' && material.linkRelacionado) {
      if (isYouTubeLink(material.linkRelacionado) || isVimeoLink(material.linkRelacionado)) {
        return material.linkRelacionado;
      }
    }

    // Caso contrário, usar materialUrl normal
    return material.materialUrl;
  };

  // Função para obter o tipo de material considerando linkRelacionado
  const getMaterialType = (material) => {
    const materialUrl = getMaterialUrl(material);

    // Se tem linkRelacionado de YouTube/Vimeo, definir tipo baseado na URL
    if (material.source === 'student' && material.linkRelacionado && materialUrl === material.linkRelacionado) {
      if (isYouTubeLink(materialUrl)) return 'YOUTUBE';
      if (isVimeoLink(materialUrl)) return 'VIMEO';
    }

    // Caso contrário, usar materialType original
    return material.materialType?.toUpperCase();
  };

  // Função para verificar se é especialista
  const isEspecialista = (material) => {
    if (material?.source === 'admin') return true;
    const especialistaEmails = [
      'admin@conecta.com', 'professor@conecta.com', 'especialista@conecta.com'
    ];
    const especialistaNomes = [
      'Conecta Admin', 'Conecta Admin Master', 'Professor', 'Especialista'
    ];
    return (
      especialistaEmails.includes(material?.authorEmail) ||
      especialistaNomes.includes(material?.authorName) ||
      material?.isEspecialista === true
    );
  };

  const getSemanaLabel = (material) => {
    const rawSemana = material?.semana || material?.curso;
    if (!rawSemana) return '';

    const semana = String(rawSemana).trim();
    if (!semana) return '';

    const match = semana.match(/semana\s*(\d+)/i);
    return match ? `Semana ${match[1]}` : semana;
  };

  useEffect(() => {
    // Redirecionar se não estiver logado
    if (user === null) {
      navigate('/login');
      return;
    }

    // Se ainda está carregando o usuário, esperar
    if (user === undefined) {
      return;
    }

    const fetchFavoritos = async () => {
      setLoading(true);
      try {
        console.log('🔍 Buscando favoritos para usuário:', user.uid);

        // 1. BUSCAR CONTEÚDOS INDIVIDUAIS FAVORITADOS
        const conteudosFavoritosData = [];

        // Buscar favoritos em materials (admin)
        const materialsRef = collection(db, 'materials');
        const materialsSnapshot = await getDocs(materialsRef);

        for (const materialDoc of materialsSnapshot.docs) {
          const favRef = doc(db, 'materials', materialDoc.id, 'favorites', user.uid);
          const favSnap = await getDoc(favRef);

          if (favSnap.exists()) {
            const materialData = materialDoc.data();
            conteudosFavoritosData.push({
              id: materialDoc.id,
              ...materialData,
              source: 'admin',
              favoritedAt: favSnap.data().data
            });
          }
        }

        // Buscar favoritos em student_materials
        const studentMaterialsRef = collection(db, 'student_materials');
        const studentSnapshot = await getDocs(studentMaterialsRef);

        for (const studentDoc of studentSnapshot.docs) {
          const favRef = doc(db, 'student_materials', studentDoc.id, 'favorites', user.uid);
          const favSnap = await getDoc(favRef);

          if (favSnap.exists()) {
            const data = studentDoc.data();
            conteudosFavoritosData.push({
              id: studentDoc.id,
              title: data.titulo,
              description: data.descricao,
              materialType: data.materialType,
              materialUrl: data.arquivoURL || data.videoUrl,
              linkRelacionado: data.linkRelacionado,
              coverImage: data.coverImage || data.arquivoURL || data.videoUrl,
              authorName: data.authorName,
              authorEmail: data.authorEmail,
              createdAt: data.createdAt,
              subcategory: data.subcategoria,
              category: data.categoria,
              source: 'student',
              conteudo: data.conteudo,
              resposta: data.resposta,
              videoResposta: data.videoResposta,
              hasThumbnail: data.hasThumbnail,
              isEspecialista: data.isEspecialista,
              semana: data.semana || data.curso || '',
              favoritedAt: favSnap.data().data
            });
          }
        }

        console.log('⭐ Conteúdos individuais favoritados:', conteudosFavoritosData.length);

        // Ordenar por data de favoritamento (mais recentes primeiro)
        conteudosFavoritosData.sort((a, b) => {
          const dateA = a.favoritedAt?.toDate ? a.favoritedAt.toDate() : new Date(a.favoritedAt);
          const dateB = b.favoritedAt?.toDate ? b.favoritedAt.toDate() : new Date(b.favoritedAt);
          return dateB - dateA;
        });

        setConteudosFavoritados(conteudosFavoritosData);
        setTotalConteudosFavoritos(conteudosFavoritosData.length);

        // 2. BUSCAR MATERIAIS DAS SUBCATEGORIAS FAVORITAS (lógica original)
        if (userFavorites && userFavorites.length > 0) {
          console.log('🔍 Buscando materiais para subcategorias favoritas:', userFavorites);

          // Buscar materiais dos professores/admin para as subcategorias favoritas
          const materialsData = materialsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            source: 'admin'
          }));

          const materiaisFiltrados = materialsData.filter(m =>
            userFavorites.includes(m.subcategory)
          );

          // Buscar materiais aprovados dos alunos para as subcategorias favoritas
          const studentQuery = query(
            studentMaterialsRef,
            where('status', '==', 'aprovado')
          );

          const studentQuerySnapshot = await getDocs(studentQuery);
          const studentMaterials = studentQuerySnapshot.docs
            .map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: data.titulo,
                description: data.descricao,
                materialType: data.materialType,
                materialUrl: data.arquivoURL || data.videoUrl,
                linkRelacionado: data.linkRelacionado,
                coverImage: data.coverImage || data.arquivoURL || data.videoUrl,
                authorName: data.authorName,
                authorEmail: data.authorEmail,
                createdAt: data.createdAt,
                subcategory: data.subcategoria,
                category: data.categoria,
                source: 'student',
                conteudo: data.conteudo,
                resposta: data.resposta,
                videoResposta: data.videoResposta,
                hasThumbnail: data.hasThumbnail,
                isEspecialista: data.isEspecialista,
                semana: data.semana || data.curso || ''
              };
            })
            .filter(material => userFavorites.includes(material.subcategory));

          // Combinar todos os materiais das subcategorias
          const todosMateriais = [...materiaisFiltrados, ...studentMaterials];

          // Agrupar por subcategoria favorita
          const materiaisAgrupados = {};
          userFavorites.forEach(subcategoria => {
            const materiaisDaSubcategoria = todosMateriais.filter(material =>
              material.subcategory === subcategoria
            );

            if (materiaisDaSubcategoria.length > 0) {
              materiaisAgrupados[subcategoria] = materiaisDaSubcategoria.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                return dateB - dateA;
              });
            }
          });

          setMateriaisPorSubcategoria(materiaisAgrupados);
          setTotalMateriais(todosMateriais.length);
        }

      } catch (error) {
        console.error('⚠ Erro ao carregar favoritos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavoritos();
  }, [user, userFavorites, navigate]);

  // Filtrar materiais pela busca
  const normalizedBusca = normalizeText(busca);
  const materiaisFiltrados = {};
  Object.keys(materiaisPorSubcategoria).forEach(subcategoria => {
    const materiaisDaSubcategoria = materiaisPorSubcategoria[subcategoria].filter(material =>
      normalizeText(material.title).includes(normalizedBusca) ||
      normalizeText(material.description).includes(normalizedBusca) ||
      normalizeText(material.authorName).includes(normalizedBusca)
    );
    if (materiaisDaSubcategoria.length > 0) {
      materiaisFiltrados[subcategoria] = materiaisDaSubcategoria;
    }
  });

  // Filtrar conteúdos favoritados pela busca
  const conteudosFiltrados = conteudosFavoritados.filter(material =>
    normalizeText(material.title).includes(normalizedBusca) ||
    normalizeText(material.description).includes(normalizedBusca) ||
    normalizeText(material.authorName).includes(normalizedBusca)
  );

  const handleVerConteudo = (material) => {
    navigate(`/conteudo/${material.id}`);
  };

  const handleVerTodosSubcategoria = (subcategoria) => {
    navigate(`/materiais/${encodeURIComponent(subcategoria)}`);
  };

  const handleRemoverFavorito = (subcategoria) => {
    if (window.confirm(`Deseja remover "${subcategoria}" dos seus favoritos?`)) {
      toggleFavorite(subcategoria);
    }
  };

  // Função otimizada para obter preview
  const getStaticPreview = (material) => {
    const materialType = getMaterialType(material);
    const materialUrl = getMaterialUrl(material);

    if (material.coverImage && material.coverImage !== materialUrl && material.hasThumbnail) {
      return material.coverImage;
    }

    if (material.coverImage && material.coverImage !== materialUrl) {
      return material.coverImage;
    }

    if (materialType === 'YOUTUBE' && materialUrl) {
      const videoId = materialUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
    }

    if (materialType === 'VIMEO' && materialUrl) {
      const vimeoId = materialUrl.match(/vimeo\.com\/(\d+)/)?.[1];
      return vimeoId ? `https://i.vimeocdn.com/video/${vimeoId}_640x360.jpg` : null;
    }

    if (['PNG', 'JPG', 'JPEG'].includes(materialType) && materialUrl) {
      return materialUrl;
    }

    if (['PDF'].includes(materialType)) {
      return null;
    }

    return null;
  };

  const getCoverImage = (material) => {
    return getStaticPreview(material);
  };

  const getTypeIcon = (materialType) => {
    const type = materialType?.toUpperCase();
    switch (type) {
      case 'YOUTUBE':
      case 'VIMEO':
        return <FaPlay />;
      case 'PDF':
        return <FaFilePdf />;
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return <FaImage />;
      default:
        return <FaEye />;
    }
  };

  const getTypeBadgeClass = (materialType) => {
    const type = materialType?.toUpperCase();
    switch (type) {
      case 'YOUTUBE':
      case 'VIMEO':
        return 'type-video';
      case 'PDF':
        return 'type-pdf';
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return 'type-image';
      default:
        return 'type-default';
    }
  };

  const formatDate = (dateData) => {
    if (!dateData) return 'Data não disponível';

    let date;
    if (dateData.seconds) {
      date = new Date(dateData.seconds * 1000);
    } else if (dateData.toDate) {
      date = dateData.toDate();
    } else if (typeof dateData === 'string') {
      date = new Date(dateData);
    } else {
      date = new Date(dateData);
    }

    return date.toLocaleDateString('pt-BR');
  };

  // Renderizar um card de material
  const renderMaterialCard = (material, showFavoriteBadge = false) => {
    const coverImage = getCoverImage(material);
    const materialType = getMaterialType(material);
    const verificado = isEspecialista(material);
    const semanaLabel = getSemanaLabel(material);

    return (
      <div className="material-card" key={`${material.source}-${material.id}`}>
        <div className="cover-wrapper">
          {coverImage ? (
            <img
              src={coverImage}
              alt={material.title}
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = e.target.nextSibling;
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
          ) : null}

          <div className={`cover-placeholder ${getTypeBadgeClass(materialType)}`}
            style={{ display: coverImage ? 'none' : 'flex' }}>
            <div className="placeholder-icon">
              {getTypeIcon(materialType)}
            </div>
            <div className="placeholder-text">
              <span className="material-type">{materialType || 'CONTEÚDO'}</span>
              <span className="material-title">{material.title}</span>
            </div>
          </div>

          <div className={`type-badge ${getTypeBadgeClass(materialType)}`}>
            {getTypeIcon(materialType)}
            <span>{materialType}</span>
          </div>

          {showFavoriteBadge && (
            <div
              className="favorite-badge"
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'rgba(220, 38, 127, 0.9)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '0.7rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                zIndex: 2
              }}
            >
              <FaHeart /> Favorito
            </div>
          )}

          {material.source === 'student' && !verificado && (
            <div className="student-badge">
              <FaUserGraduate />
              <span>Material do Aluno</span>
            </div>
          )}

          {verificado && (
            <div className="expert-badge">
              <FaCheckCircle />
              <span>Especialista</span>
            </div>
          )}

          {material.source === 'student' && material.linkRelacionado && (
            <div
              className="link-badge"
              style={{
                position: 'absolute',
                bottom: '8px',
                left: '8px',
                background: 'rgba(156, 39, 176, 0.9)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '0.7rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                zIndex: 2
              }}
            >
              🔗 Link
            </div>
          )}

          <div className="hover-overlay">
            <button
              className="preview-button"
              onClick={() => handleVerConteudo(material)}
            >
              <FaEye />
              Ver Conteúdo
            </button>
          </div>
        </div>

        <div className="material-info">
          <h3 className="material-title">{material.title}</h3>
          {semanaLabel && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.25rem 0.6rem',
                borderRadius: '14px',
                fontSize: '0.65rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                background: 'rgba(250, 204, 21, 0.95)',
                color: '#111827',
                border: '1px solid rgba(250, 204, 21, 0.55)',
                boxShadow: '0 2px 8px rgba(250, 204, 21, 0.2)',
                width: 'fit-content'
              }}
              title={`Conteudo referente a ${semanaLabel}`}
            >
              <span>{semanaLabel}</span>
            </div>
          )}

          <div className="material-meta">
            <div className="meta-item">
              <FaUser className="meta-icon" />
              <span>{material.authorName || 'Conecta Admin'}</span>
              {material.source === 'student' && !verificado && (
                <span className="author-type">(Aluno)</span>
              )}
              {verificado && (
                <span className="author-type-expert">
                  <span className="dot"></span>
                  (Especialista)
                </span>
              )}
            </div>

            <div className="meta-item">
              <FaCalendarAlt className="meta-icon" />
              <span>{formatDate(material.createdAt)}</span>
            </div>
          </div>

          <p className="material-description">
            {material.description?.length > 80
              ? `${material.description.substring(0, 80)}...`
              : material.description || 'Sem descrição disponível'
            }
          </p>

          <button
            className="ver-conteudo-button"
            onClick={() => handleVerConteudo(material)}
          >
            <FaEye />
            Ver Conteúdo
          </button>
        </div>
      </div>
    );
  };

  // Loading do usuário
  if (user === undefined) {
    return (
      <div className="favoritos-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // Usuário não logado
  if (user === null) {
    return (
      <div className="favoritos-page">
        <div className="empty-state">
          <div className="empty-icon">
            <FaHeart />
          </div>
          <h3>Acesso restrito</h3>
          <p>Você precisa estar logado para ver seus favoritos.</p>
          <button
            className="login-button"
            onClick={() => navigate('/login')}
          >
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="favoritos-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando seus favoritos...</p>
        </div>
      </div>
    );
  }

  const temFavoritos = (userFavorites && userFavorites.length > 0) || totalConteudosFavoritos > 0;

  return (
    <div className="favoritos-page">
      <header className="favoritos-header">
        <div className="favoritos-title-container">
          <FaHeart className="favoritos-icon" />
          <div>
            <h1 className="favoritos-titulo">Meus Favoritos</h1>
            <p className="favoritos-subtitulo">
              {userFavorites.length} subcategorias • {totalMateriais} materiais • {totalConteudosFavoritos} conteúdos favoritados
            </p>
          </div>
        </div>
      </header>

      {!temFavoritos ? (
        <div className="empty-state">
          <div className="empty-icon">
            <FaRegHeart />
          </div>
          <h3>Nenhum favorito ainda</h3>
          <p>Você ainda não salvou nenhuma subcategoria ou conteúdo como favorito.</p>
          <p>Explore as categorias e marque suas subcategorias preferidas com ❤️ ou favorite conteúdos individuais com <FaBookmark /></p>
          <div className="empty-actions">
            <button
              className="voltar-home-button"
              onClick={() => navigate('/')}
            >
              <FaHome />
              Explorar Categorias
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="favoritos-busca">
            <div className="search-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar nos seus favoritos..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
          </div>

          <div className="favoritos-content">
            {/* SEÇÃO: CONTEÚDOS FAVORITOS INDIVIDUAIS */}
            {conteudosFiltrados.length > 0 && (
              <section className="subcategoria-section">
                <div className="subcategoria-header">
                  <div className="subcategoria-title-container">
                    <h2 className="subcategoria-titulo">
                      <FaBookmark style={{ marginRight: '0.5rem', color: '#dc2626' }} />
                      Conteúdos Favoritados
                    </h2>
                    <span className="materiais-count">{conteudosFiltrados.length} conteúdos</span>
                  </div>
                </div>

                <div className="materiais-grid">
                  {conteudosFiltrados.slice(0, 12).map((material) =>
                    renderMaterialCard(material, true)
                  )}
                </div>

                {conteudosFiltrados.length > 12 && (
                  <div className="ver-mais-container">
                    <button
                      className="ver-mais-button"
                      onClick={() => {
                        // Implementar paginação ou modal com todos os favoritos
                        console.log('Ver mais conteúdos favoritados');
                      }}
                    >
                      Ver mais {conteudosFiltrados.length - 12} conteúdos favoritados
                    </button>
                  </div>
                )}
              </section>
            )}

            {/* SEÇÃO: MATERIAIS DAS SUBCATEGORIAS FAVORITAS */}
            {Object.keys(materiaisFiltrados).length > 0 && (
              Object.entries(materiaisFiltrados).map(([subcategoria, materiais]) => (
                <section key={subcategoria} className="subcategoria-section">
                  <div className="subcategoria-header">
                    <div className="subcategoria-title-container">
                      <h2 className="subcategoria-titulo">{subcategoria}</h2>
                      <span className="materiais-count">{materiais.length} materiais</span>
                    </div>
                    <div className="subcategoria-actions">
                      <button
                        className="ver-todos-button"
                        onClick={() => handleVerTodosSubcategoria(subcategoria)}
                      >
                        Ver todos <FaArrowRight />
                      </button>
                      <button
                        className="remover-favorito-button"
                        onClick={() => handleRemoverFavorito(subcategoria)}
                        title="Remover dos favoritos"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className="materiais-grid">
                    {materiais.slice(0, 6).map((material) =>
                      renderMaterialCard(material, false)
                    )}
                  </div>

                  {materiais.length > 6 && (
                    <div className="ver-mais-container">
                      <button
                        className="ver-mais-button"
                        onClick={() => handleVerTodosSubcategoria(subcategoria)}
                      >
                        Ver mais {materiais.length - 6} materiais de {subcategoria}
                      </button>
                    </div>
                  )}
                </section>
              ))
            )}

            {/* ESTADO VAZIO QUANDO TEM FAVORITOS MAS A BUSCA NÃO RETORNA RESULTADOS */}
            {temFavoritos && conteudosFiltrados.length === 0 && Object.keys(materiaisFiltrados).length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">
                  <FaSearch />
                </div>
                <h3>Nenhum resultado encontrado</h3>
                <p>
                  {busca
                    ? `Não encontramos materiais com "${busca}" nos seus favoritos.`
                    : 'Nenhum material disponível nos seus favoritos.'
                  }
                </p>
                <div className="empty-actions">
                  {busca && (
                    <button
                      className="clear-search-button"
                      onClick={() => setBusca('')}
                    >
                      Limpar busca
                    </button>
                  )}
                  <button
                    className="voltar-home-button"
                    onClick={() => navigate('/')}
                  >
                    <FaHome />
                    Explorar Mais
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default MeusFavoritos;
